import {
  Children,
  Fragment,
  isValidElement,
  ReactElement,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

export interface SelectProps {
  value?: string;
  defaultValue?: string;
  onChange?: (e: { target: { value: string } }) => void;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  id?: string;
  name?: string;
  'aria-label'?: string;
  placeholder?: string;
  portalZIndex?: number;
  variant?: 'default' | 'status';
  renderValue?: (value: string, label: ReactNode) => ReactNode;
  children?: ReactNode;
}

interface ParsedOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

interface OptionElementProps {
  value?: string | number;
  disabled?: boolean;
  children?: ReactNode;
}

function parseOptions(children: ReactNode): ParsedOption[] {
  const result: ParsedOption[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === Fragment) {
      result.push(...parseOptions((child as ReactElement<OptionElementProps>).props.children));
      return;
    }
    if (child.type === 'option') {
      const props = (child as ReactElement<OptionElementProps>).props;
      result.push({
        value: String(props.value ?? ''),
        label: props.children ?? '',
        disabled: props.disabled,
      });
    }
  });
  return result;
}

const TRIGGER_BASE =
  'w-full flex items-center justify-between gap-2 ' +
  'rounded-input bg-surface-base/60 text-sm text-left ' +
  'px-field-x py-field-y ' +
  'border transition-colors duration-fast ease-smooth ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40 focus-visible:border-accent-primary/50';

export function Select({
  value,
  defaultValue,
  onChange,
  disabled,
  error,
  className,
  id,
  name,
  'aria-label': ariaLabel,
  placeholder,
  portalZIndex = 11050,
  variant = 'default',
  renderValue,
  children,
}: SelectProps) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<string>(defaultValue ?? '');
  const currentValue = isControlled ? value! : internalValue;

  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState<number | null>(null);
  const [popupPos, setPopupPos] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    placement: 'top' | 'bottom';
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const options = useMemo(() => parseOptions(children), [children]);
  const selectedOption = options.find((o) => o.value === currentValue);

  const triggerLabel: ReactNode = (() => {
    if (selectedOption && selectedOption.value !== '') return selectedOption.label;
    if (selectedOption && selectedOption.value === '') {
      return selectedOption.label || placeholder || '';
    }
    return placeholder || '';
  })();

  const isPlaceholderShown = !selectedOption || selectedOption.value === '';

  const handleSelect = (newValue: string) => {
    if (!isControlled) setInternalValue(newValue);
    onChange?.({ target: { value: newValue } });
    setOpen(false);
    triggerRef.current?.focus();
  };

  // Pozycja popupu względem viewport (fixed) - aktualizowana przy scroll/resize.
  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const gap = 6;
      const desiredHeight = Math.min(288, options.length * 40 + 12);
      const availableBelow = window.innerHeight - rect.bottom - gap - 8;
      const availableAbove = rect.top - gap - 8;
      const placement = availableBelow < Math.min(desiredHeight, 160) && availableAbove > availableBelow
        ? 'top'
        : 'bottom';
      const availableHeight = placement === 'top' ? availableAbove : availableBelow;
      const maxHeight = Math.max(96, Math.min(desiredHeight, availableHeight));
      setPopupPos({
        top: placement === 'top' ? Math.max(8, rect.top - gap - maxHeight) : rect.bottom + gap,
        left: rect.left,
        width: rect.width,
        maxHeight,
        placement,
      });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, options.length]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger = containerRef.current?.contains(target);
      const insidePopup = popupRef.current?.contains(target);
      if (!insideTrigger && !insidePopup) {
        setOpen(false);
      }
    };
    const enabled = options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i !== -1);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (enabled.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIdx((prev) => {
          const cur = prev == null ? -1 : enabled.indexOf(prev);
          return enabled[(cur + 1) % enabled.length];
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIdx((prev) => {
          const cur = prev == null ? enabled.length : enabled.indexOf(prev);
          return enabled[(cur - 1 + enabled.length) % enabled.length];
        });
      } else if (e.key === 'Enter' && focusIdx != null) {
        e.preventDefault();
        const opt = options[focusIdx];
        if (opt && !opt.disabled) handleSelect(opt.value);
      } else if (e.key === 'Tab') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, options, focusIdx]);

  useEffect(() => {
    if (!open) {
      setFocusIdx(null);
      return;
    }
    const idx = options.findIndex((o) => o.value === currentValue && !o.disabled);
    setFocusIdx(idx >= 0 ? idx : null);
  }, [open]);

  const triggerClasses = [
    variant === 'status' ? 'flex items-center gap-2 px-4 py-2 bg-surface-raised hover:bg-surface-hover/40 border border-border rounded-lg transition-colors text-sm focus-visible:ring-2 focus-visible:ring-accent-primary/40' : TRIGGER_BASE,
    error ? 'border-danger/60 focus-visible:ring-danger/30 focus-visible:border-danger' : 'border-border hover:border-border-strong',
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    isPlaceholderShown ? 'text-text-muted' : 'text-text-primary',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        id={id}
        name={name}
        aria-label={ariaLabel}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((o) => !o);
        }}
        className={triggerClasses}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{renderValue ? renderValue(currentValue, triggerLabel) : triggerLabel || '\u00A0'}</span>
        <ChevronDown
          className={`w-4 h-4 text-text-muted shrink-0 transition-transform duration-fast ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && popupPos && createPortal(
        <div
          ref={popupRef}
          role="listbox"
          style={{
            position: 'fixed',
            top: popupPos.top,
            left: popupPos.left,
            width: popupPos.width,
            maxHeight: popupPos.maxHeight,
            zIndex: portalZIndex,
          }}
          className={`overflow-y-auto rounded-xl bg-surface-raised border border-border shadow-glass backdrop-blur-xl py-1.5 animate-scale-in ${
            popupPos.placement === 'top' ? 'origin-bottom' : 'origin-top'
          }`}
        >
          {options.length === 0 && (
            <div className="px-3 py-2 text-sm text-text-muted">Brak opcji</div>
          )}
          {options.map((opt, idx) => {
            const isSelected = opt.value === currentValue;
            const isFocused = focusIdx === idx;
            return (
              <button
                key={opt.value === '' ? `__empty-${idx}` : opt.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                disabled={opt.disabled}
                onClick={() => {
                  if (!opt.disabled) handleSelect(opt.value);
                }}
                onMouseEnter={() => !opt.disabled && setFocusIdx(idx)}
                className={[
                  'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors',
                  opt.disabled
                    ? 'opacity-50 cursor-not-allowed text-text-disabled'
                    : isSelected
                      ? 'text-accent-primary'
                      : 'text-text-secondary hover:text-text-primary',
                  isFocused && !opt.disabled
                    ? isSelected
                      ? 'bg-accent-primary/10'
                      : 'bg-surface-hover text-text-primary'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span className="truncate flex-1">{opt.label || '\u00A0'}</span>
                {isSelected && <Check className="w-4 h-4 text-accent-primary shrink-0" aria-hidden="true" />}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
